import { useEffect, useMemo, useState, useCallback, lazy, Suspense, memo } from "react";

import classes from "./DatePickerCell.module.css";
import { formatDate, getItemFromStorageSimple } from "../../../lib/script";

import classesCell from "./BodyCell.module.css";
import { dueDateDifference } from "../../../lib/dateScripts";
import DateDisplayWithNmuberOfDays from "../../common/DateDisplayWithNmuberOfDays";
import { diffCalendarDays, parseApiDateOrDateTime } from "../../../lib/dateParsing";
import { useWorkQEditSession } from "../../../lib/hooks/useWorkQEditSession";

const DatePickerCellEditor = lazy(() => import("./DatePickerCellEditor"));

function parseDateValue(value) {
    return parseApiDateOrDateTime(value);
}

function formatDateTimeForApi(value) {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;

    const pad = (n) => String(n).padStart(2, "0");
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

function getDateOfIncidentColorRules() {
    const settings = getItemFromStorageSimple("settings");
    const configured =
        settings?.frontend_options?.DATE_OF_INCIDENT_COLOR || settings?.frontend_options?.date_of_incident_color;

    if (!configured || typeof configured !== "object") {
        return null;
    }

    return {
        purple: configured.purple ?? null,
        red: configured.red ?? null,
        orange: configured.orange ?? null,
        yellow: configured.yellow ?? null,
    };
}

function getMrqEntryDateColorRules() {
    const settings = getItemFromStorageSimple("settings");
    const configured =
        settings?.frontend_options?.MRQ_ENTRY_DATE_COLOR || settings?.frontend_options?.mrq_entry_date_color;

    if (!configured || typeof configured !== "object") {
        return null;
    }

    return {
        red: configured.red ?? null,
        yellow: configured.yellow ?? null,
    };
}

/** Avoid parsing settings for every date cell on every row paint. */
let _dpColorRulesWinRef = null;
let _dpColorRulesLsStr = null;
let _dpIncidentRulesCache = undefined;
let _dpMrqRulesCache = undefined;

function invalidateDatePickerColorRulesCache() {
    _dpIncidentRulesCache = undefined;
    _dpMrqRulesCache = undefined;
}

function ensureDatePickerColorRulesCacheFresh() {
    if (typeof window !== "undefined" && window.systemSettings) {
        if (window.systemSettings !== _dpColorRulesWinRef) {
            _dpColorRulesWinRef = window.systemSettings;
            invalidateDatePickerColorRulesCache();
        }
        return;
    }
    let s = null;
    try {
        s = localStorage.getItem("settings");
    } catch {
        s = null;
    }
    if (s !== _dpColorRulesLsStr) {
        _dpColorRulesLsStr = s;
        invalidateDatePickerColorRulesCache();
    }
}

function getDateOfIncidentColorRulesCached() {
    ensureDatePickerColorRulesCacheFresh();
    if (_dpIncidentRulesCache !== undefined) {
        return _dpIncidentRulesCache;
    }
    _dpIncidentRulesCache = getDateOfIncidentColorRules();
    return _dpIncidentRulesCache;
}

function getMrqEntryDateColorRulesCached() {
    ensureDatePickerColorRulesCacheFresh();
    if (_dpMrqRulesCache !== undefined) {
        return _dpMrqRulesCache;
    }
    _dpMrqRulesCache = getMrqEntryDateColorRules();
    return _dpMrqRulesCache;
}

function getIncidentAgeDays(dateValue) {
    if (!dateValue) return null;
    const diff = diffCalendarDays(dateValue, new Date())
    if (diff == null) return null
    return diff
}

function matchesRange(days, range) {
    if (!range || typeof range !== "object") return false;
    const minOk = range.min == null || days >= Number(range.min);
    const maxOk = range.max == null || days <= Number(range.max);
    return minOk && maxOk;
}

function DatePickerCell({
    date,
    action,
    identifier,
    disabled,
    item,
    workq,
    isDateTime = false,
    hideEmptyPlaceholder = false,
    /** Keeps date + days badge grouped at the end (avoids space-between stretch in narrow table cells). */
    pairDaysAtEnd = false,
    prefix = null,
    /** Native title tooltip, e.g. "Created on 8/3/2026" when showing a recording date. */
    hoverTitle = null,
}) {
    const parsedStartDate = useMemo(() => parseDateValue(date), [date]);
    const [localSelectedDate, setLocalSelectedDate] = useState(parsedStartDate);
    const [editingNextFu, setEditingNextFu] = useState(false);
    useWorkQEditSession(editingNextFu);

    /** Follow-up column: show formatted value from picker state immediately (Redux row can lag optimistic update). */
    const nextFuDisplaySource = useMemo(() => {
        if (identifier?.field !== "next_fu_date") return null;
        if (localSelectedDate != null && !(localSelectedDate instanceof Date && Number.isNaN(localSelectedDate.getTime()))) {
            const formatted = isDateTime ? formatDateTimeForApi(localSelectedDate) : formatDate(localSelectedDate);
            return formatted || date || null;
        }
        return date || null;
    }, [identifier?.field, localSelectedDate, date, isDateTime]);

    useEffect(() => {
        setLocalSelectedDate(parsedStartDate);
    }, [parsedStartDate]);

    useEffect(() => {
        setEditingNextFu(false);
    }, [parsedStartDate, identifier?.field]);

    const startDate = localSelectedDate;

    let maxDueDate = null;
    let minDueDate = null;
    let nextDateBorder = "";
    let isDateDisabled = identifier?.field === "next_fu_date" ? false : disabled;

    if (identifier?.field === "next_fu_date") {
        if (item && item.task_notes && item.task_notes.length > 0 && workq?.note_type_id) {
            maxDueDate = item.task_notes[0].due_date;
            minDueDate = new Date();
        } else {
            maxDueDate = null;
            minDueDate = new Date();
        }
        const borderSrc = nextFuDisplaySource ?? date;
        if (borderSrc) {
            nextDateBorder = dueDateDifference(borderSrc);
        }
    }
    const isNextAppointmentForNonCase =
        identifier?.field === "next_appointment_date" &&
        Number(item?.case?.is_case) === 0 &&
        Boolean(date) &&
        date !== "0000-00-00";
    let dateOfIncidentColorClass = "";
    if (identifier?.field === "incident_date" && date && date !== "0000-00-00") {
        const daysFromIncident = getIncidentAgeDays(date);
        const rules = getDateOfIncidentColorRulesCached();
        if (daysFromIncident != null && daysFromIncident >= 0 && rules) {
            if (matchesRange(daysFromIncident, rules.purple)) {
                dateOfIncidentColorClass = classes.border_purple;
            } else if (matchesRange(daysFromIncident, rules.red)) {
                dateOfIncidentColorClass = classes.border_red;
            } else if (matchesRange(daysFromIncident, rules.orange)) {
                dateOfIncidentColorClass = classes.border_orange;
            } else if (matchesRange(daysFromIncident, rules.yellow)) {
                dateOfIncidentColorClass = classes.border_yellow;
            }
        }
    }
    let mrqEntryDateColorClass = "";
    if (identifier?.field === "mrq_entry_date" && date && date !== "0000-00-00") {
        const daysFromMrqEntryDate = getIncidentAgeDays(date);
        const rules = getMrqEntryDateColorRulesCached();
        if (daysFromMrqEntryDate != null && daysFromMrqEntryDate >= 0 && rules) {
            if (matchesRange(daysFromMrqEntryDate, rules.red)) {
                mrqEntryDateColorClass = classes.border_red;
            } else if (matchesRange(daysFromMrqEntryDate, rules.yellow)) {
                mrqEntryDateColorClass = classes.border_yellow;
            }
        }
    }

    const datePickerChangehandler = useCallback(
        (dateValue) => {
            setLocalSelectedDate(dateValue || null);

            if (action) {
                const value = isDateTime ? formatDateTimeForApi(dateValue) : formatDate(dateValue);
                action({
                    data: { value },
                    identifier: identifier,
                });
            }
        },
        [action, identifier, isDateTime],
    );

    const shellClass = `${classes.datepicker_cell_container} ${classesCell.body_cell} ${classes[nextDateBorder]} ${dateOfIncidentColorClass} ${mrqEntryDateColorClass} ${isNextAppointmentForNonCase ? `${classes.background_purple} ${classes.border_purple}` : ""}`;

    const isNextFu = identifier?.field === "next_fu_date";

    if (isNextFu && editingNextFu) {
        return (
            <div data-workq-date-editor="true">
            <Suspense
                fallback={
                    <div className={shellClass}>
                        <span className="loader" style={{ display: "inline-block", minHeight: 24, minWidth: 24 }} />
                    </div>
                }
            >
                <DatePickerCellEditor
                    startDate={startDate}
                    datePickerChangehandler={datePickerChangehandler}
                    isDateDisabled={isDateDisabled}
                    maxDueDate={maxDueDate}
                    minDueDate={minDueDate}
                    isDateTime={isDateTime}
                    nextDateBorder={nextDateBorder}
                    dateOfIncidentColorClass={dateOfIncidentColorClass}
                    mrqEntryDateColorClass={mrqEntryDateColorClass}
                    isNextAppointmentForNonCase={isNextAppointmentForNonCase}
                    onExitEditMode={() => {
                        queueMicrotask(() => setEditingNextFu(false));
                    }}
                />
            </Suspense>
            </div>
        );
    }

    const activateNextFu = () => {
        if (!isDateDisabled) {
            setEditingNextFu(true);
        }
    };

    return (
        <div className={shellClass}>
            {isNextFu && nextFuDisplaySource && (
                <button
                    type="button"
                    className={classes.next_fu_display_trigger}
                    onClick={activateNextFu}
                    disabled={isDateDisabled}
                    title="Edit follow-up date"
                >
                    <DateDisplayWithNmuberOfDays date={nextFuDisplaySource} hasTime={isDateTime} />
                    <span className={`${classes.datepicker_cell_icon} ${isDateTime ? classes.datepicker_cell_icon_datetime : ""}`}>
                        <i className={isDateTime ? "ti ti-clock-hour-3" : "ti ti-calendar"}></i>
                    </span>
                </button>
            )}

            {!isNextFu && date && (
                <div
                    className={`${classes.date_row} ${pairDaysAtEnd ? classes.date_row_end : ""}`}
                    title={hoverTitle || undefined}
                >
                    {prefix}
                    <DateDisplayWithNmuberOfDays date={date} hasTime={isDateTime} textRight={pairDaysAtEnd} />
                </div>
            )}

            {isNextFu && !nextFuDisplaySource && (
                <button
                    type="button"
                    className={classes.next_fu_display_trigger}
                    onClick={activateNextFu}
                    disabled={isDateDisabled}
                    title="Set follow-up date"
                >
                    -
                    <span className={`${classes.datepicker_cell_icon} ${isDateTime ? classes.datepicker_cell_icon_datetime : ""}`}>
                        <i className={isDateTime ? "ti ti-clock-hour-3" : "ti ti-calendar"}></i>
                    </span>
                </button>
            )}

            {!isNextFu && !date && !hideEmptyPlaceholder && <>-</>}
        </div>
    );
}

function datePickerCellPropsAreEqual(prev, next) {
    return (
        prev.date === next.date &&
        prev.disabled === next.disabled &&
        prev.isDateTime === next.isDateTime &&
        prev.hideEmptyPlaceholder === next.hideEmptyPlaceholder &&
        prev.pairDaysAtEnd === next.pairDaysAtEnd &&
        prev.prefix === next.prefix &&
        prev.hoverTitle === next.hoverTitle &&
        prev.action === next.action &&
        prev.item === next.item &&
        prev.workq === next.workq &&
        prev.identifier?.case_id === next.identifier?.case_id &&
        prev.identifier?.folder === next.identifier?.folder &&
        prev.identifier?.field === next.identifier?.field &&
        prev.identifier?.type === next.identifier?.type
    );
}

export default memo(DatePickerCell, datePickerCellPropsAreEqual);
